/**
 * Shared API response types used by both web and desktop apps.
 */

// ---------------------------------------------------------------------------
// Task types
// ---------------------------------------------------------------------------

export const TODO_GROUP_SLUG = "todo";
export const THIS_SESSION_GROUP_SLUG = "in-session";
export const COMPLETED_GROUP_SLUG = "completed";

export type Task = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  estimatedTime?: number;
  timeSpent?: number;
  dueDate?: Date;
  completedAt?: Date;
  orderIndex?: number;
  externalServiceName?: string;
  externalTaskId?: string;
  externalTaskUrl?: string;
  isActiveTask?: boolean;
  isOptimistic?: boolean;
};

export type ExternalTaskSource = "linear" | "todoist" | string;

export interface ServiceMetadata {
  displayName: string;
  icon: string;
  redirectPath: string;
}

export interface ServiceMetadataWithConnection extends ServiceMetadata {
  source: string;
  isConnected: boolean;
  serviceId?: string;
  email?: string | null;
}

export interface GetTasksParams {
  page?: number;
  limit?: number;
  groupSlug?: string;
  search?: string;
}

export interface GetTasksResponse {
  data: Task[];
  total: number;
  page: number;
  limit: number;
  canShareTask?: boolean;
}

export interface CreateTaskInput {
  name: string;
  description?: string;
  estimatedTime?: number;
  dueDate?: Date;
  externalSourceId?: string;
  externalTaskId?: string;
  externalTaskUrl?: string;
}

export interface ExternalTasksResponse {
  message: string;
  data: {
    data: unknown[];
    nextCursor: string | null;
    existingTaskCount?: number;
    existingTodoistCount?: number;
    existingLinearIssueCount?: number;
  };
}

// ---------------------------------------------------------------------------
// Session / Points types
// ---------------------------------------------------------------------------

export interface BonusEntry {
  id: string;
  userId: string;
  sourceSessionId: string | null;
  awardDate: string;
  usableOnDate: string;
  bonusPoints: number;
  status: "active" | "consumed" | "expired";
  appliedSessionId: string | null;
  appliedAt: string | null;
  expiredByDate: string | null;
  expiredAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  [key: string]: unknown;
}

export interface BonusAvailableResponse {
  bonus: { bonusPoints: number } | null;
}

export interface BonusHistoryResponse {
  history: BonusEntry[];
}

export interface BonusTomorrowResponse {
  bonus: { bonusPoints: number; usableOnDate: string; awardDate: string } | null;
}

export interface AvailableActivityInfo {
  points: number;
  frequency: number;
  activity_count: number;
  within_frequency_limit: boolean;
}

export type AvailableSessionPoints = Record<string, AvailableActivityInfo>;

export interface StartupPointsResponse {
  points: number;
  activities: string[];
  missingActivities?: { activity: string; points: number }[];
  targetGoal: number | null;
  /** Streak points earned at session start (when streak >= 2) */
  streakPoints?: number;
  /** Streak day count (2, 3, 4, 5, 6, ...) */
  streakDay?: number;
}

export interface SubscriptionStatus {
  isActive: boolean;
  status: string;
  planName?: string;
  trialEndsAt?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Chat types
// ---------------------------------------------------------------------------

export interface Channel {
  id: string;
  slug?: string;
  name?: string;
  type?: string;
  unreadMessageCount?: number;
  [key: string]: unknown;
}

export interface UserChannelsResponse {
  channels: Channel[];
}

export interface ChatMessage {
  id: string;
  channelId: string;
  content: string;
  userId: string;
  createdAt: string;
  [key: string]: unknown;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

// ---------------------------------------------------------------------------
// Calendar types
// ---------------------------------------------------------------------------

export interface CalendarEvent {
  id: string;
  summary: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Playlist types
// ---------------------------------------------------------------------------

export interface Playlist {
  id: string;
  name?: string;
  title?: string;
  description?: string | null;
  url?: string;
  active?: boolean;
  isActive?: boolean;
  key?: string;
  producer?: string | null;
  [key: string]: unknown;
}

export interface PlaylistStats {
  playlist: {
    id: string;
    title: string;
    description: string | null;
    url: string;
    active: boolean;
    isActive?: boolean;
    key: string;
    producer: string | null;
    tracks?: Array<{
      id: string;
      title: string;
      description?: string | null;
      duration?: number | null;
      artist?: string | null;
      url: string;
      key: string;
      active: boolean;
    }> | null;
  } | null;
  totalFocusMinutes: number;
  tasksCompleted: number;
  sessionsCount: number;
  averageSessionLength: number;
  pointsEarned: number;
  productivity?: number;
  isMostPopular?: boolean;
  isYourFavorite?: boolean;
  isMostProductive?: boolean;
}

export interface PlaylistAnalyticsResponse {
  playlists: PlaylistStats[];
  badges: {
    mostPopular: PlaylistStats["playlist"];
    yourFavorite: PlaylistStats["playlist"];
    mostProductive: (NonNullable<PlaylistStats["playlist"]> & { productivity?: number }) | null;
  };
}

// ---------------------------------------------------------------------------
// Other types
// ---------------------------------------------------------------------------

export interface AppVersionResponse {
  version: string;
  [key: string]: unknown;
}

export interface Personality {
  id: string;
  name: string;
  personality: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Active task types
// ---------------------------------------------------------------------------

export interface ActiveTask {
  id: string;
  name: string;
  description?: string | null;
  timeSpent: number;
  estimatedTime: number;
  completedAt?: string | null;
  isActiveTask: boolean;
}

export interface ActiveTaskResponse {
  data: ActiveTask | null;
}

export interface UserSettings {
  id?: string;
  focusTime: number;
  breakTime: number;
  timerMode: "time-to-work" | "time-to-next-break";
  defaultTaskDuration?: number;
  nudgeIntervalMinutes?: number;
  phoneQRPrompt?: boolean;
  productivityGoal?: number;
  assistantAudioEnabled?: boolean;
  smartAssistantAudioVolume?: number;
  wordsOfEncouragement?: boolean;
  /** @deprecated Use `websiteNudgeEnabled` (TX-5183). */
  phoneAndWebSiteNudge?: boolean;
  phoneNudgeEnabled?: boolean;
  /** "Website & App Nudges" (TX-5183, renamed TX-5253), on by default. */
  websiteNudgeEnabled?: boolean;
  /** "Spoken Session Transitions" (TX-5253), on by default. */
  spokenSessionTransitions?: boolean;
  /** "Spoken Typing Reminder" (TX-5253): the desktop app reads it. */
  spokenTypingReminder?: boolean;
  /** "Spoken Onboarding Narration" (TX-5253), on by default. */
  onboardingNarrationEnabled?: boolean;
  generalTaskHelp?: boolean;
  meetingRemindersEnabled?: boolean;
  meetingReminderTime?: number;
  activePlaylistId?: string;
  musicVolume?: number;
  timerSoundsEnabled?: boolean;
  timerSoundsVolume?: number;
  taskSimplificationThreshold?: number;
  cameraEnabled?: boolean;
  favoriteBackgroundImage?: string;
  dayStartTime?: string;
  dayEndTime?: string;
  timezone?: string;
  locale?: string;
  skinTone?: number;
  timeFormat?: string;
  pullNextTask?: boolean;
  urlDistractionMode?: boolean;
  /** When true, desktop app reports foreground app time during focus sessions. */
  desktopAppTrackingEnabled?: boolean;
  /** "Let Sukha help in apps on your Mac" (TX-5065). Off by default. */
  desktopContentHelpEnabled?: boolean;
  /** Last time the desktop app contacted the server. Null = never installed/used. */
  desktopAppLastSeenAt?: string | Date | null;
  /** Whether Settings offers "Download for Mac" — the admin switch (TX-5510). */
  desktopAppDownloadEnabled?: boolean;
  /** Desktop control is on for the platform — the admin switch (TX-5078). Off, nothing of it shows. */
  desktopControlAvailable?: boolean;
  /** The member's own switch for desktop control (TX-5078). On unless they turn it off. */
  desktopControlEnabled?: boolean;
  shareCompletedTask?: boolean;
  dailyEmailEnabled?: boolean;
  unreadMessagesEmailEnabled?: boolean;
  measurementUnits?: string;
  maxTasksToCreate?: number;
  assistantPersonality?: string;
  calendarUrl?: string | null;
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
}

/** Calendar settings from GET /user/settings/calendar (user_external_services only). */
export type CalendarSettings = {
  hasCalendar: boolean;
  linkedSources: string[];
  linkedBySource?: Record<
    string,
    Array<{ id: string; externalId: string; name?: string; active: boolean }>
  >;
};
